import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Modal, Offcanvas, Spinner, Alert } from 'react-bootstrap';
import {
  useGetCrmTaskLookupsQuery,
  useGetCrmTaskQuery,
  useCreateCrmTaskMutation,
  useUpdateCrmTaskMutation,
  useClaimCrmTaskMutation,
  useSearchCrmClientsQuery,
} from '../../app/api';
import { PRODUCTS } from './Crm';

// Shared pieces of Work -> Tasks: the task list, the New task form and the
// task drawer. Used by the Tasks page and by a client's CRM page. The server
// is api/app/routers/crm_tasks.py; who sees which task is decided there.

export const work = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  green:     '#1a7f4e',
  red:       '#b42318',
  ink:       '#0a2947',
  muted:     '#6b7280',
  hairline:  '#dde2e8',
  surface:   '#ffffff',
  canvas:    '#f3f5f8',
};

const CATEGORY_ICONS = { ...Object.fromEntries(PRODUCTS.map((p) => [p.key, p.icon])), other: 'three-dots' };
const WAITING = ['waiting_client', 'waiting_carrier'];
const CLOSED = ['done', 'cancelled'];

export const errText = (e) => (typeof e?.data?.detail === 'string' ? e.data.detail : 'Something went wrong. Please try again.');

/** Today as 'YYYY-MM-DD' in the browser's own time zone. */
export function localToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtDue = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const fmtWhen = (iso) => new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

/** Where a task sits in the "Mine" list: waiting on someone else, or by due date. */
export function dueGroup(t, today = localToday()) {
  if (WAITING.includes(t.status)) return 'waiting';
  if (!t.due_date) return 'none';
  if (t.due_date < today) return 'overdue';
  if (t.due_date === today) return 'today';
  if (t.due_date <= addDays(today, 7)) return 'week';
  return 'later';
}
export const DUE_GROUPS = [
  ['overdue', 'Overdue'], ['today', 'Today'], ['week', 'Next 7 days'], ['later', 'Later'],
  ['none', 'No due date'], ['waiting', 'Waiting on client / carrier'],
];

export function useTaskLookups() {
  const q = useGetCrmTaskLookupsQuery();
  const data = q.data;
  const maps = useMemo(() => {
    if (!data) return null;
    return {
      ...data,
      categoryLabel: Object.fromEntries(data.categories.map((c) => [c.key, c.label])),
      statusLabel: Object.fromEntries(data.statuses.map((s) => [s.key, s.label])),
      personName: Object.fromEntries(data.people.map((p) => [p.uuid, p.name])),
      queueName: Object.fromEntries(data.queues.map((x) => [x.id, x.name])),
    };
  }, [data]);
  return { ...q, lookups: maps };
}

function DueBadge({ task }) {
  if (!task.due_date) return <span style={{ color: work.muted }}>-</span>;
  const g = CLOSED.includes(task.status) ? 'done' : dueGroup({ ...task, status: 'open' });
  const color = g === 'overdue' ? work.red : g === 'today' ? work.goldenrod : work.ink;
  return (
    <span style={{ color, fontWeight: g === 'overdue' || g === 'today' ? 700 : 400, whiteSpace: 'nowrap' }}>
      {g === 'today' ? 'Today' : fmtDue(task.due_date)}
    </span>
  );
}
DueBadge.propTypes = { task: PropTypes.object.isRequired };

function CategoryPill({ category, label, type }) {
  return (
    <span title={label} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.75rem', color: work.ink, whiteSpace: 'nowrap' }}>
      <i className={`bi bi-${CATEGORY_ICONS[category] || 'tag'}`} style={{ color: work.goldenrod }} />
      {type || label}
    </span>
  );
}
CategoryPill.propTypes = { category: PropTypes.string, label: PropTypes.string, type: PropTypes.string };

function StatusPill({ status, label }) {
  const bg = CLOSED.includes(status) ? 'rgba(26,127,78,0.12)'
    : WAITING.includes(status) ? 'rgba(208,155,44,0.16)'
    : status === 'in_progress' ? 'rgba(0,105,166,0.12)' : 'rgba(10,41,71,0.06)';
  return (
    <span style={{ background: bg, borderRadius: 4, padding: '0.1rem 0.45rem', fontSize: '0.72rem', fontWeight: 600, color: work.ink, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}
StatusPill.propTypes = { status: PropTypes.string.isRequired, label: PropTypes.string };

/** Who has the task: the owner, else the queue it waits in. */
function Holder({ task }) {
  if (task.owner_uuid) {
    return (
      <span style={{ whiteSpace: 'nowrap' }}>
        {task.owner_name || 'Former employee'}
        {task.assignee_names?.length > 0 && <span style={{ color: work.muted }}> +{task.assignee_names.length}</span>}
      </span>
    );
  }
  return <span style={{ color: work.goldenrod, fontWeight: 600, whiteSpace: 'nowrap' }}><i className="bi bi-inbox me-1" />{task.queue_name || 'Queue'}</span>;
}
Holder.propTypes = { task: PropTypes.object.isRequired };

/**
 * A list of tasks. `grouped` splits open work into the due-date sections of
 * the Mine tab; otherwise it is one table in the server's order.
 */
export function TaskTable({ tasks, lookups, onOpen, onClaim, showClient = true, grouped = false, isMobile = false, empty }) {
  if (!tasks.length) {
    return <div style={{ padding: '1.5rem 1rem', color: work.muted, fontSize: '0.88rem', textAlign: 'center' }}>{empty || 'No tasks here.'}</div>;
  }
  const today = localToday();
  const sections = grouped
    ? DUE_GROUPS.map(([key, label]) => ({ key, label, rows: tasks.filter((t) => dueGroup(t, today) === key) })).filter((s) => s.rows.length)
    : [{ key: 'all', label: null, rows: tasks }];

  const row = (t) => (
    <tr key={t.id} onClick={() => onOpen(t.id)} style={{ cursor: 'pointer', opacity: CLOSED.includes(t.status) ? 0.6 : 1 }}>
      <td style={{ width: 70 }}><DueBadge task={t} /></td>
      <td style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: work.ink, overflowWrap: 'anywhere' }}>
          {t.priority === 'high' && <i className="bi bi-exclamation-circle-fill me-1" style={{ color: work.red }} title="High priority" />}
          {t.subject}
        </div>
        {isMobile && (
          <div style={{ fontSize: '0.75rem', color: work.muted }}>
            {[showClient && t.client_name, t.task_type || lookups.categoryLabel[t.category]].filter(Boolean).join(' · ')}
          </div>
        )}
      </td>
      {!isMobile && showClient && <td style={{ color: work.ink }}>{t.client_name || <span style={{ color: work.muted }}>-</span>}</td>}
      {!isMobile && <td><CategoryPill category={t.category} label={lookups.categoryLabel[t.category]} type={t.task_type} /></td>}
      <td><Holder task={t} /></td>
      {!isMobile && <td><StatusPill status={t.status} label={lookups.statusLabel[t.status]} /></td>}
      {onClaim && (
        <td style={{ width: 1 }}>
          {!t.owner_uuid && (
            <button type="button" className="btn btn-sm" onClick={(e) => { e.stopPropagation(); onClaim(t.id); }}
                    style={{ background: work.deepBlue, color: 'white', fontWeight: 600, whiteSpace: 'nowrap' }}>
              Take it
            </button>
          )}
        </td>
      )}
    </tr>
  );

  return (
    <div className="table-responsive">
      <table className="table table-sm table-hover align-middle" style={{ fontSize: '0.85rem', marginBottom: 0 }}>
        <thead>
          <tr style={{ color: work.muted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            <th>Due</th><th>Task</th>
            {!isMobile && showClient && <th>Client</th>}
            {!isMobile && <th>Type</th>}
            <th>With</th>
            {!isMobile && <th>Status</th>}
            {onClaim && <th />}
          </tr>
        </thead>
        {sections.map((s) => (
          <tbody key={s.key}>
            {s.label && (
              <tr>
                <td colSpan={7} style={{
                  background: work.canvas, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px',
                  color: s.key === 'overdue' ? work.red : work.muted,
                }}>
                  {s.label} <span style={{ fontWeight: 400 }}>({s.rows.length})</span>
                </td>
              </tr>
            )}
            {s.rows.map(row)}
          </tbody>
        ))}
      </table>
    </div>
  );
}
TaskTable.propTypes = {
  tasks: PropTypes.arrayOf(PropTypes.object).isRequired,
  lookups: PropTypes.object.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClaim: PropTypes.func,
  showClient: PropTypes.bool,
  grouped: PropTypes.bool,
  isMobile: PropTypes.bool,
  empty: PropTypes.node,
};

/** Find a CRM client for a new task. Same scoped search as Work -> CRM. */
function ClientSearch({ onPick }) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  const { data, isFetching } = useSearchCrmClientsQuery(
    { q: debounced, status_group: 'all', limit: 8 }, { skip: debounced.length < 2 });
  return (
    <div>
      <input className="form-control form-control-sm" placeholder="Search by name, email or city (optional)" value={q}
             onChange={(e) => setQ(e.target.value)} />
      {debounced.length >= 2 && (
        <div style={{ border: `1px solid ${work.hairline}`, borderRadius: 6, marginTop: 4, background: work.surface, maxHeight: 240, overflowY: 'auto' }}>
          {isFetching && <div style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem', color: work.muted }}>Searching...</div>}
          {!isFetching && !data?.clients?.length && (
            <div style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem', color: work.muted }}>No clients match in the offices you can see.</div>
          )}
          {!isFetching && data?.clients?.map((c) => (
            <button key={c.id} type="button" onClick={() => onPick({ id: c.id, name: c.display_name })}
                    style={{ display: 'block', width: '100%', textAlign: 'left', border: 0, borderTop: `1px solid ${work.hairline}`, background: 'transparent', padding: '0.4rem 0.6rem' }}>
              <div style={{ fontSize: '0.85rem', color: work.ink, fontWeight: 600 }}>{c.display_name}</div>
              <div style={{ fontSize: '0.72rem', color: work.muted }}>
                {[c.source_name, [c.city, c.state].filter(Boolean).join(', '), c.status].filter(Boolean).join(' · ')}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
ClientSearch.propTypes = { onPick: PropTypes.func.isRequired };

const label = { fontSize: '0.72rem', fontWeight: 700, color: work.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3, display: 'block' };

/** Pick several people (the extra assignees). */
function PeoplePicker({ people, value, onChange, exclude }) {
  const options = people.filter((p) => !value.includes(p.uuid) && p.uuid !== exclude);
  const name = Object.fromEntries(people.map((p) => [p.uuid, p.name]));
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: value.length ? 4 : 0 }}>
        {value.map((u) => (
          <span key={u} style={{ background: 'rgba(10,41,71,0.07)', borderRadius: 4, padding: '0.1rem 0.4rem', fontSize: '0.8rem' }}>
            {name[u] || 'Former employee'}
            <button type="button" aria-label="Remove" onClick={() => onChange(value.filter((x) => x !== u))}
                    style={{ border: 0, background: 'none', padding: '0 0 0 4px', color: work.muted }}>
              <i className="bi bi-x" />
            </button>
          </span>
        ))}
      </div>
      <select className="form-select form-select-sm" value="" onChange={(e) => e.target.value && onChange([...value, e.target.value])}>
        <option value="">Add someone...</option>
        {options.map((p) => <option key={p.uuid} value={p.uuid}>{p.name}</option>)}
      </select>
    </div>
  );
}
PeoplePicker.propTypes = {
  people: PropTypes.arrayOf(PropTypes.object).isRequired,
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  exclude: PropTypes.string,
};

/**
 * New task. The TYPE decides the category and suggests the queue, so a CSA
 * makes three choices: type, who, when. `client` ({id, name}) pre-fills the
 * client when opened from a CRM client page.
 */
export function NewTaskModal({ show, onHide, onCreated, client }) {
  // The form lives in its own component: Modal unmounts it when hidden, so
  // every opening starts from a blank form.
  return (
    <Modal show={show} onHide={onHide} size="lg">
      <NewTaskForm onHide={onHide} onCreated={onCreated} client={client} />
    </Modal>
  );
}
NewTaskModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  client: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
};

function NewTaskForm({ onHide, onCreated, client }) {
  const { lookups } = useTaskLookups();
  const [createTask, { isLoading }] = useCreateCrmTaskMutation();
  const [f, setF] = useState({
    client: client || null, category: '', typeId: '', subject: '', details: '', assignTo: 'queue', queueId: '',
    ownerUuid: '', assignees: [], dueDate: '', priority: 'normal', caseId: '',
  });
  const [error, setError] = useState(null);
  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));

  if (!lookups) return <Modal.Body style={{ textAlign: 'center' }}><Spinner animation="border" size="sm" /></Modal.Body>;
  const types = lookups.types.filter((t) => t.active && (!f.category || t.category === f.category));

  const pickType = (id) => {
    const t = lookups.types.find((x) => String(x.id) === String(id));
    if (!t) { set({ typeId: '' }); return; }
    const queueOk = t.default_queue_id && lookups.queues.some((q) => q.id === t.default_queue_id && q.active);
    // The subject follows the type until someone types their own.
    const prev = lookups.types.find((x) => String(x.id) === f.typeId);
    set({
      typeId: String(t.id),
      category: t.category,
      subject: !f.subject || (prev && f.subject === prev.name) ? t.name : f.subject,
      ...(queueOk ? { assignTo: 'queue', queueId: String(t.default_queue_id) } : { assignTo: f.queueId ? f.assignTo : 'person' }),
    });
  };

  const submit = async () => {
    setError(null);
    if (!f.category) { setError('Pick a type (or at least a category).'); return; }
    if (!f.subject.trim()) { setError('Give the task a subject.'); return; }
    if (f.assignTo === 'queue' && !f.queueId) { setError('Pick a queue, or give it to a person.'); return; }
    if (f.assignTo === 'person' && !f.ownerUuid) { setError('Pick who the task is for.'); return; }
    try {
      const res = await createTask({
        client_id: f.client?.id || null,
        subject: f.subject.trim(),
        details: f.details.trim() || null,
        category: f.category,
        task_type_id: f.typeId ? Number(f.typeId) : null,
        priority: f.priority,
        due_date: f.dueDate || null,
        owner_uuid: f.assignTo === 'person' ? f.ownerUuid : null,
        queue_id: f.assignTo === 'queue' ? Number(f.queueId) : null,
        assignees: f.assignees,
        fsc_case_id: f.caseId.trim() || null,
      }).unwrap();
      onCreated?.(res.id);
      onHide();
    } catch (e) {
      setError(errText(e));
    }
  };

  return (
    <>
      <Modal.Header closeButton><Modal.Title style={{ fontSize: '1.1rem', fontWeight: 700 }}>New task</Modal.Title></Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" style={{ fontSize: '0.85rem' }}>{error}</Alert>}
        <div className="row g-3">
          <div className="col-12">
            <span style={label}>Client</span>
            {f.client ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <strong style={{ color: work.ink }}>{f.client.name}</strong>
                {!client && (
                  <button type="button" className="btn btn-link btn-sm p-0" onClick={() => set({ client: null })}>change</button>
                )}
              </div>
            ) : <ClientSearch onPick={(c) => set({ client: c })} />}
          </div>
          <div className="col-md-4">
            <span style={label}>Category</span>
            <select className="form-select form-select-sm" value={f.category}
                    onChange={(e) => set({ category: e.target.value, typeId: '' })}>
              <option value="">All categories</option>
              {lookups.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div className="col-md-8">
            <span style={label}>Type</span>
            <select className="form-select form-select-sm" value={f.typeId} onChange={(e) => pickType(e.target.value)}>
              <option value="">Pick a type...</option>
              {(f.category ? [f.category] : lookups.categories.map((c) => c.key)).map((cat) => {
                const list = types.filter((t) => t.category === cat);
                return list.length ? (
                  <optgroup key={cat} label={lookups.categoryLabel[cat]}>
                    {list.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </optgroup>
                ) : null;
              })}
            </select>
          </div>
          <div className="col-12">
            <span style={label}>Subject</span>
            <input className="form-control form-control-sm" maxLength={200} value={f.subject} onChange={(e) => set({ subject: e.target.value })} />
          </div>
          <div className="col-12">
            <span style={label}>Details</span>
            <textarea className="form-control form-control-sm" rows={3} value={f.details} onChange={(e) => set({ details: e.target.value })}
                      placeholder="What needs doing, account numbers, who to call..." />
          </div>
          <div className="col-md-6">
            <span style={label}>Give it to</span>
            <div className="btn-group btn-group-sm mb-2" role="group">
              {[['queue', 'A queue'], ['person', 'A person']].map(([k, l]) => (
                <button key={k} type="button" className={`btn ${f.assignTo === k ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => set({ assignTo: k })}>{l}</button>
              ))}
            </div>
            {f.assignTo === 'queue' ? (
              <select className="form-select form-select-sm" value={f.queueId} onChange={(e) => set({ queueId: e.target.value })}>
                <option value="">Pick a queue...</option>
                {lookups.queues.filter((q) => q.active).map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
              </select>
            ) : (
              <select className="form-select form-select-sm" value={f.ownerUuid} onChange={(e) => set({ ownerUuid: e.target.value })}>
                <option value="">Pick a person...</option>
                {lookups.people.map((p) => <option key={p.uuid} value={p.uuid}>{p.uuid === lookups.me ? `${p.name} (me)` : p.name}</option>)}
              </select>
            )}
          </div>
          <div className="col-md-6">
            <span style={label}>Also keep in the loop</span>
            <PeoplePicker people={lookups.people} value={f.assignees} onChange={(v) => set({ assignees: v })}
                          exclude={f.assignTo === 'person' ? f.ownerUuid : undefined} />
          </div>
          <div className="col-md-4">
            <span style={label}>Due</span>
            <input type="date" className="form-control form-control-sm" value={f.dueDate} onChange={(e) => set({ dueDate: e.target.value })} />
          </div>
          <div className="col-md-4">
            <span style={label}>Priority</span>
            <select className="form-select form-select-sm" value={f.priority} onChange={(e) => set({ priority: e.target.value })}>
              <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
            </select>
          </div>
          <div className="col-md-4">
            <span style={label}>Salesforce Case ID</span>
            <input className="form-control form-control-sm" maxLength={18} value={f.caseId} placeholder="optional"
                   onChange={(e) => set({ caseId: e.target.value })} />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onHide}>Cancel</button>
        <button type="button" className="btn btn-sm" disabled={isLoading} onClick={submit}
                style={{ background: work.deepBlue, color: 'white', fontWeight: 600 }}>
          {isLoading ? <Spinner animation="border" size="sm" /> : 'Create task'}
        </button>
      </Modal.Footer>
    </>
  );
}
NewTaskForm.propTypes = {
  onHide: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  client: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
};

/** One line of a task's history, in words. Events never carry the task's text. */
function describe(e, lookups) {
  const d = e.detail || {};
  const person = (u) => (u ? (lookups.personName[u] || 'a former employee') : 'nobody');
  const queue = (q) => lookups.queueName[q] || 'a queue';
  switch (e.kind) {
    case 'created': return d.owner ? `created it for ${person(d.owner)}` : `created it in ${queue(d.queue)}`;
    case 'claimed': return `took it from ${queue(d.queue)}`;
    case 'status': return `changed status to ${lookups.statusLabel[d.to] || d.to}`;
    case 'owner': return d.to ? `gave it to ${person(d.to)}` : 'handed it back to the queue';
    case 'queue': return d.to ? `moved it to ${queue(d.to)}` : 'took it out of its queue';
    case 'due_date': return d.to ? `set the due date to ${fmtDue(d.to)}` : 'removed the due date';
    case 'priority': return `set priority to ${d.to}`;
    case 'type': return 'changed the type';
    case 'case': return d.to ? `linked Salesforce Case ${d.to}` : 'unlinked the Salesforce Case';
    case 'assignees': {
      const parts = [];
      if (d.added?.length) parts.push(`added ${d.added.map(person).join(', ')}`);
      if (d.removed?.length) parts.push(`removed ${d.removed.map(person).join(', ')}`);
      return parts.join('; ') || 'changed who is in the loop';
    }
    case 'edited': return `edited the ${d.field || 'task'}`;
    default: return e.kind;
  }
}

/**
 * One task: change status, reassign, edit, and its history. Every change saves
 * straight away and is written to the history.
 */
export function TaskDrawer({ taskId, onClose, onChanged }) {
  const { lookups } = useTaskLookups();
  const { data: t, isLoading, isError, refetch } = useGetCrmTaskQuery(taskId, { skip: !taskId });
  const [update, { isLoading: saving }] = useUpdateCrmTaskMutation();
  const [claim, { isLoading: claiming }] = useClaimCrmTaskMutation();
  const [error, setError] = useState(null);
  const [edit, setEdit] = useState(null);   // {subject, details} while editing text

  useEffect(() => { setError(null); setEdit(null); }, [taskId]);

  const save = async (changes) => {
    setError(null);
    try {
      await update({ id: taskId, changes }).unwrap();
      await refetch();
      onChanged?.();
      return true;
    } catch (e) {
      setError(errText(e));
      return false;
    }
  };
  const takeIt = async () => {
    setError(null);
    try {
      await claim(taskId).unwrap();
      await refetch();
      onChanged?.();
    } catch (e) {
      setError(errText(e));
    }
  };

  const busy = saving || claiming;
  const types = t && lookups ? lookups.types.filter((x) => x.active || x.id === t.task_type_id) : [];

  return (
    <Offcanvas show={Boolean(taskId)} onHide={onClose} placement="end" style={{ width: 'min(520px, 100vw)' }}>
      <Offcanvas.Header closeButton style={{ borderBottom: `3px solid ${work.goldenrod}` }}>
        <Offcanvas.Title style={{ fontSize: '1rem', fontWeight: 700, color: work.ink }}>Task</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body style={{ fontSize: '0.86rem' }}>
        {(isLoading || !lookups) && <div style={{ textAlign: 'center', padding: '2rem' }}><Spinner animation="border" size="sm" /></div>}
        {isError && <Alert variant="warning">This task was not found, or you can no longer see it.</Alert>}
        {t && lookups && (
          <>
            {error && <Alert variant="danger" style={{ fontSize: '0.85rem' }}>{error}</Alert>}

            {edit ? (
              <div className="mb-3">
                <input className="form-control form-control-sm mb-2" maxLength={200} value={edit.subject}
                       onChange={(e) => setEdit({ ...edit, subject: e.target.value })} />
                <textarea className="form-control form-control-sm mb-2" rows={5} value={edit.details}
                          onChange={(e) => setEdit({ ...edit, details: e.target.value })} />
                <button type="button" className="btn btn-sm me-2" disabled={busy}
                        style={{ background: work.deepBlue, color: 'white', fontWeight: 600 }}
                        onClick={async () => { if (await save({ subject: edit.subject, details: edit.details })) setEdit(null); }}>
                  Save
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEdit(null)}>Cancel</button>
              </div>
            ) : (
              <div className="mb-3">
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <h5 style={{ fontWeight: 700, color: work.ink, margin: 0, flex: 1, overflowWrap: 'anywhere' }}>{t.subject}</h5>
                  <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setEdit({ subject: t.subject, details: t.details || '' })}>
                    <i className="bi bi-pencil" /> Edit
                  </button>
                </div>
                {t.client_name && (
                  <div style={{ marginTop: 4 }}>
                    {t.crm_client_id
                      ? <Link to={`/work/crm/${t.crm_client_id}`} style={{ color: work.tealBlue, fontWeight: 600 }}>{t.client_name}</Link>
                      : <strong>{t.client_name}</strong>}
                  </div>
                )}
                <div style={{ whiteSpace: 'pre-wrap', marginTop: 8, color: t.details ? work.ink : work.muted, overflowWrap: 'anywhere' }}>
                  {t.details || 'No details.'}
                </div>
              </div>
            )}

            {!t.owner_uuid && (
              <div style={{ background: 'rgba(208,155,44,0.12)', borderRadius: 6, padding: '0.6rem 0.75rem', marginBottom: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <i className="bi bi-inbox" style={{ color: work.goldenrod }} />
                <span style={{ flex: 1 }}>Waiting in <strong>{t.queue_name}</strong> for someone to take it.</span>
                <button type="button" className="btn btn-sm" disabled={busy} onClick={takeIt}
                        style={{ background: work.deepBlue, color: 'white', fontWeight: 600 }}>Take it</button>
              </div>
            )}

            <div className="row g-2 mb-3">
              <div className="col-6">
                <span style={label}>Status</span>
                <select className="form-select form-select-sm" value={t.status} disabled={busy} onChange={(e) => save({ status: e.target.value })}>
                  {lookups.statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
              <div className="col-6">
                <span style={label}>Due</span>
                <input type="date" className="form-control form-control-sm" value={t.due_date || ''} disabled={busy}
                       onChange={(e) => save({ due_date: e.target.value || null })} />
              </div>
              <div className="col-6">
                <span style={label}>Owner</span>
                <select className="form-select form-select-sm" value={t.owner_uuid || ''} disabled={busy}
                        onChange={(e) => save({ owner_uuid: e.target.value || null })}>
                  <option value="" disabled={!t.queue_id}>{t.queue_id ? 'Nobody yet (in the queue)' : 'Pick a person...'}</option>
                  {t.owner_uuid && !lookups.personName[t.owner_uuid] && <option value={t.owner_uuid}>{t.owner_name || 'Former employee'}</option>}
                  {lookups.people.map((p) => <option key={p.uuid} value={p.uuid}>{p.uuid === lookups.me ? `${p.name} (me)` : p.name}</option>)}
                </select>
              </div>
              <div className="col-6">
                <span style={label}>Queue</span>
                <select className="form-select form-select-sm" value={t.queue_id || ''} disabled={busy}
                        onChange={(e) => save({ queue_id: e.target.value ? Number(e.target.value) : null })}>
                  <option value="" disabled={!t.owner_uuid}>No queue</option>
                  {lookups.queues.filter((q) => q.active || q.id === t.queue_id).map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
                </select>
              </div>
              <div className="col-6">
                <span style={label}>Type</span>
                <select className="form-select form-select-sm" value={t.task_type_id || ''} disabled={busy}
                        onChange={(e) => {
                          const ty = types.find((x) => String(x.id) === e.target.value);
                          if (ty) save({ task_type_id: ty.id, category: ty.category });
                        }}>
                  {!t.task_type_id && <option value="">{lookups.categoryLabel[t.category]} (no type)</option>}
                  {lookups.categories.map((c) => {
                    const list = types.filter((x) => x.category === c.key);
                    return list.length ? (
                      <optgroup key={c.key} label={c.label}>
                        {list.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                      </optgroup>
                    ) : null;
                  })}
                </select>
              </div>
              <div className="col-6">
                <span style={label}>Priority</span>
                <select className="form-select form-select-sm" value={t.priority} disabled={busy} onChange={(e) => save({ priority: e.target.value })}>
                  <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
                </select>
              </div>
              <div className="col-12">
                <span style={label}>Also in the loop</span>
                <PeoplePicker people={lookups.people} value={t.assignees} exclude={t.owner_uuid || undefined}
                              onChange={(v) => save({ assignees: v })} />
              </div>
              {t.fsc_case_id && (
                <div className="col-12" style={{ color: work.muted }}>
                  Salesforce Case <strong style={{ color: work.ink }}>{t.fsc_case_id}</strong>
                </div>
              )}
            </div>

            <span style={label}>History</span>
            <div style={{ borderLeft: `2px solid ${work.hairline}`, paddingLeft: '0.75rem' }}>
              {[...t.events].reverse().map((e, i) => (
                <div key={i} style={{ marginBottom: '0.45rem' }}>
                  <div><strong>{e.actor_name}</strong> {describe(e, lookups)}</div>
                  <div style={{ fontSize: '0.74rem', color: work.muted }}>{fmtWhen(e.at)}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
}
TaskDrawer.propTypes = {
  taskId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onChanged: PropTypes.func,
};
